/*
  A FILE THAT IS NO LONGER THERE LOADS THE PAGE AGAIN, ONCE (MTR #622).

  Since #622 a renter's page arrives in a file of its own, named with the hash of its contents. So a
  deploy in the seconds between this page being served and that file being asked for leaves her asking
  for a name that has just changed, and what she gets is a blank screen.

  Vite raises `vite:preloadError` for exactly this, and loading the page again — which fetches the new
  names — is the remedy in its own documentation. The only thing worth deciding here is when NOT to:

    · a file that is genuinely gone must not be able to put her in a loop, so we try once per tab;
    · with no memory of having tried we cannot promise to stop, so we do not start.

  Proven on 2026-10-20 against the built product, with the search page's file answered 404: one 404,
  one reload, 123 homes on the screen. With it answered 404 for ever: one reload and no more.
*/

/* The one thing a tab remembers about this. Not exported: what is written is checked through the
   box the caller hands in, so nothing outside has to agree on the name. */
const LOADED_AGAIN = 'stayoo.again'

/*
  A FETCH SHE DID NOT ASK FOR MUST NOT TAKE HER PAGE AWAY (independent review of MTR #673).

  Since #673 the home page fetches the enquiry form in the background, before she has pressed
  anything. Vite's loader raises `vite:preloadError` for that fetch like any other — and it raises it
  whether or not the caller catches the rejection — so a background fetch that fails on a weak
  connection would have reloaded the page she was reading, and on a connection that bad the reload
  can land on the browser's own "no internet" page. She would have lost the home, and the dates she
  had picked, over a file she never asked for.

  So a fetch marked optional says so while it is in flight, and a failure during it is not a reason to
  load the page again. Both of the home page's attempts at the form are marked — the background one
  and the one on her press — because a page that reloads itself the moment she presses Send inquiry
  takes away the dates she picked and tells her nothing; the thing waiting for that file says what
  happened instead, and asks her to reload, which is the same remedy taken by her rather than to her.

  The cost, named: if the page's own file happens to fail in the same moment as one of these, that
  reload is skipped too — and then whoever is waiting on that file shows its own failure, which is
  the right place for it.
*/
let optional = 0
export const whileOptional = (promise) => {
  optional += 1
  const done = () => { optional -= 1 }
  return promise.then((v) => { done(); return v }, (e) => { done(); throw e })
}

/**
 * Whether to load the page again, having written down that we did.
 * `box` is a `sessionStorage`-shaped thing, or null when the browser keeps nothing.
 */
export function shouldLoadAgain(box) {
  if (optional > 0) return false
  let tried = null
  try { tried = box?.getItem(LOADED_AGAIN) ?? null } catch { return false }
  if (tried) return false
  try { box.setItem(LOADED_AGAIN, '1') } catch { return false }
  return true
}
